import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { SITE_ID } from '../lib/constants';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };

    // site_kullanicilari tablosunda bu kullanıcının bu siteye kaydı var mı kontrol et
    const userId = data.user?.id;
    if (userId) {
      const { data: siteUser, error: siteErr } = await supabase
        .from('site_kullanicilari')
        .select('id')
        .eq('user_id', userId)
        .eq('site_id', SITE_ID)
        .maybeSingle();

      if (siteErr) {
        await supabase.auth.signOut();
        return { error: 'Hesap doğrulanırken bir hata oluştu.' };
      }

      if (!siteUser) {
        await supabase.auth.signOut();
        return { error: 'BU_SITEDE_HESAP_YOK' };
      }
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    });
    if (error) return { error: error.message };

    // Kayıt başarılıysa site_kullanicilari tablosuna ekle
    const userId = data.user?.id;
    if (userId) {
      const { error: insertErr } = await supabase
        .from('site_kullanicilari')
        .insert({
          user_id: userId,
          site_id: SITE_ID,
          ad_soyad: name,
        });

      if (insertErr) {
        // Kayıt oluşturuldu ama site tablosuna eklenemedi — yine de devam et
        console.error('site_kullanicilari insert error:', insertErr.message);
      }
    }

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/profil`,
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
